import { ChangeDetectionStrategy, Component, computed, ElementRef, HostListener, input, output } from '@angular/core';
import moment from 'moment';
import { IActiveTicket } from 'src/app/models';
import { ActiveTicketCategory } from 'src/app/models/enums';
import { LiteralService } from 'src/app/services/literal/literal.service';
import { TooltipPosition } from 'src/app/shared/tooltip/tooltip-position';
import {
  ACTIVE_TICKET_CATEGORY_STYLES,
  activeTicketsCategory,
  activeTicketsSummary,
  activeTicketTypeCountKey,
  suggestionTypeIcon,
  suggestionUrl,
  suggestionVisibilityIcon,
} from 'src/app/utils/suggestionTickets';
import { images } from 'src/images';

@Component({
  selector: 'app-active-tickets-chip',
  templateUrl: './active-tickets-chip.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ActiveTicketsChipComponent {
  private static readonly LIST_WIDTH = 320;

  readonly tickets = input<IActiveTicket[]>([]);
  readonly compact = input<boolean>(false);
  readonly openInNewTab = input<boolean>(false);
  readonly ticketSelected = output<IActiveTicket>();

  public images = images;
  public TooltipPosition = TooltipPosition;
  public suggestionTypeIcon = suggestionTypeIcon;
  public suggestionVisibilityIcon = suggestionVisibilityIcon;
  public suggestionUrl = suggestionUrl;
  public listOpened = false;
  public listPosition = { top: 0, left: 0 };

  readonly category = computed(() => activeTicketsCategory(this.tickets()));
  readonly style = computed(() => ACTIVE_TICKET_CATEGORY_STYLES[this.category()]);

  readonly summaryTooltip = computed(() => activeTicketsSummary(this.tickets(), this.literalService));

  readonly badgeLabel = computed(() => {
    const count = this.tickets().length;
    if (this.category() === ActiveTicketCategory.INCIDENT)
      return this.literalService.get(count === 1 ? 'suggestions.activeTickets.one' : 'suggestions.activeTickets.many', true, { count });
    return this.literalService.get(activeTicketTypeCountKey(this.tickets()[0].type, count), true, { count });
  });

  constructor(
    public literalService: LiteralService,
    private elementRef: ElementRef<HTMLElement>,
  ) {}

  @HostListener('document:click', ['$event'])
  closeListOnOutsideClick(event: Event) {
    if (this.listOpened && !this.elementRef.nativeElement.contains(event.target as Node))
      this.listOpened = false;
  }

  toggleList(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    const chipBounds = (event.currentTarget as HTMLElement | null)?.getBoundingClientRect();
    if (chipBounds)
      this.listPosition = {
        top: chipBounds.bottom + 4,
        left: Math.max(8, Math.min(chipBounds.left, window.innerWidth - ActiveTicketsChipComponent.LIST_WIDTH - 8)),
      };
    this.listOpened = !this.listOpened;
  }

  selectTicket(event: Event, ticket: IActiveTicket) {
    event.stopPropagation();
    this.listOpened = false;
    if (this.openInNewTab()) return;
    event.preventDefault();
    this.ticketSelected.emit(ticket);
  }

  ticketDetails(ticket: IActiveTicket) {
    return [
      moment(ticket.createdAt).format('DD/MM HH:mm'),
      ticket.userSuggesterName,
      ticket.expeditionId ? null : this.literalService.get('suggestions.activeTickets.wholeService', true),
    ]
      .filter(Boolean)
      .join(' · ');
  }

  ticketTitle(ticket: IActiveTicket) {
    return `${this.literalService.get(`suggestions.${ticket.type.toLowerCase()}`, true)}: ${this.literalService.get(`suggestions.affairs.${ticket.affair}`, true)}`;
  }
}
